import { useRef, useState, type Dispatch, type RefObject, type SetStateAction } from 'react'
import { Paperclip, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  MAX_REPLY_ATTACHMENT_BYTES,
  MAX_REPLY_ATTACHMENTS_TOTAL_BYTES,
} from '@/features/communications/attachments'
import { formatFileSize, readFileAsBase64 } from '@/features/communications/formatting'
import type { ReplyAttachmentDraft } from '@/features/communications/types'
import { getApiErrorMessage } from '@/lib/api-error'

function megabytes(bytes: number) {
  return `${Math.round(bytes / (1024 * 1024))} MB`
}

export function ReplyAttachments({
  attachments,
  onChange,
  attaching,
  onAttachingChange,
  disabled = false,
  maxFileBytes = MAX_REPLY_ATTACHMENT_BYTES,
  maxTotalBytes = MAX_REPLY_ATTACHMENTS_TOTAL_BYTES,
  maxFiles,
  accept,
  compact = false,
  pickerRef,
}: {
  attachments: ReplyAttachmentDraft[]
  onChange: Dispatch<SetStateAction<ReplyAttachmentDraft[]>>
  attaching: boolean
  onAttachingChange: (attaching: boolean) => void
  disabled?: boolean
  /** Defaults to the email limits; storefront chat takes 10 MB files, five at a time. */
  maxFileBytes?: number
  maxTotalBytes?: number
  maxFiles?: number
  accept?: string
  /** Only the chosen files and errors: the parent opens the file picker through ``pickerRef``
   * (the storefront chat reply bar on a phone). */
  compact?: boolean
  pickerRef?: RefObject<HTMLInputElement | null>
}) {
  const fileLimit = megabytes(maxFileBytes)
  const totalLimit = megabytes(maxTotalBytes)
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="min-w-0 space-y-1">
      <div className={compact ? 'contents' : 'flex flex-wrap items-center gap-2'}>
        {!compact && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || attaching}
            onClick={() => inputRef.current?.click()}
          >
            <Paperclip className="size-3.5" aria-hidden="true" />
            {attaching ? 'Reading files...' : 'Attach files'}
          </Button>
        )}
        <input
          ref={(element) => {
            inputRef.current = element
            if (pickerRef) pickerRef.current = element
          }}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          aria-label="Attach files"
          disabled={disabled || attaching}
          onChange={async (event) => {
            const input = event.target
            const files = Array.from(input.files ?? [])
            if (files.length === 0) return
            setError(null)
            onAttachingChange(true)
            try {
              let runningTotal = attachments.reduce((sum, attachment) => sum + attachment.size, 0)
              const accepted: ReplyAttachmentDraft[] = []
              for (const file of files) {
                if (maxFiles !== undefined && attachments.length + accepted.length >= maxFiles) {
                  setError(`Attach up to ${maxFiles} files; some were skipped`)
                  break
                }
                if (file.size > maxFileBytes) {
                  setError(`${file.name} is larger than ${fileLimit} and was skipped`)
                  continue
                }
                if (runningTotal + file.size > maxTotalBytes) {
                  setError(`Attachments would exceed the ${totalLimit} total limit; some were skipped`)
                  continue
                }
                const contentBase64 = await readFileAsBase64(file)
                accepted.push({
                  id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
                  filename: file.name,
                  contentType: file.type || 'application/octet-stream',
                  size: file.size,
                  contentBase64,
                })
                runningTotal += file.size
              }
              if (accepted.length > 0) onChange((previous) => [...previous, ...accepted])
            } catch (err) {
              setError(getApiErrorMessage(err, 'Failed to read file'))
            } finally {
              onAttachingChange(false)
              input.value = ''
            }
          }}
        />
        {!compact && (
          <span className="text-xs text-muted-foreground">
            {maxFiles !== undefined
              ? `Up to ${maxFiles} files, ${fileLimit} each`
              : `Up to ${fileLimit} per file, ${totalLimit} total`}
          </span>
        )}
      </div>
      {attachments.length > 0 && (
        <ul className="space-y-1">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2 py-1 text-xs"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <Paperclip className="size-3 shrink-0" aria-hidden="true" />
                <span className="truncate" title={attachment.filename}>
                  {attachment.filename}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {formatFileSize(attachment.size)}
                </span>
              </span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-8 shrink-0"
                disabled={disabled || attaching}
                onClick={() =>
                  onChange((previous) => previous.filter((file) => file.id !== attachment.id))
                }
                aria-label={`Remove ${attachment.filename}`}
                title={`Remove ${attachment.filename}`}
              >
                <X className="size-3" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
